import { useState } from "react";
import { Box, IconButton, Skeleton, Stack, TextField, Typography, CircularProgress } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import { useCurrentUserId, useSendEmailAdminReply } from "../../../hooks/useEmail";
import { formatTime, formatDateLabel, formatRelativeTime, getNumberColor } from "../../whatsapp/chatUiHelpers";

// Per-influencer view across EVERY staff member's mailbox, unlike EmailInboxPage.jsx's
// per-user Inbox — one row per thread (one thread per campaign send, see models/
// emailConversation.js), same "one row per our-account this creator has talked to" shape
// Instagram/WhatsApp already give this page. Replying still only works from the thread's
// OWN sender's mailbox (see ReplyBox below), same restriction EmailInboxPage enforces.

const EMAIL_BLUE = "#1A73E8";

const FADE_SLIDE = {
  "@keyframes fadeSlideIn": {
    "0%": { opacity: 0, transform: "translateY(8px)" },
    "100%": { opacity: 1, transform: "translateY(0)" },
  },
};

function cleanSnippet(text) {
  return (text || "").replace(/\s+/g, " ").trim();
}

function initialOf(name) {
  return (name || "?").trim().charAt(0).toUpperCase() || "?";
}

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function AttachmentChip({ attachment }) {
  return (
    <Box
      onClick={() => attachment.url && window.open(attachment.url, "_blank", "noopener,noreferrer")}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        border: "1px solid rgba(0,0,0,0.12)",
        borderRadius: "8px",
        px: 1.2,
        py: 0.6,
        cursor: attachment.url ? "pointer" : "default",
        bgcolor: "#fff",
        "&:hover": attachment.url ? { boxShadow: "0 1px 6px rgba(0,0,0,0.15)" } : undefined,
      }}
    >
      <AttachFileRoundedIcon sx={{ fontSize: 15, color: "#5F6368" }} />
      <Box sx={{ minWidth: 0 }}>
        <Typography noWrap sx={{ fontFamily: "Quicksand", fontSize: 12, fontWeight: 600, color: "#202124", maxWidth: 160 }}>
          {attachment.filename}
        </Typography>
        <Typography sx={{ fontFamily: "Quicksand", fontSize: 10, color: "#80868B" }}>
          {formatBytes(attachment.size)}
        </Typography>
      </Box>
    </Box>
  );
}

// One collapsed/expandable card per message — mirrors EmailInboxPage.jsx's EmailMessageCard,
// duplicated rather than shared since that one is coupled to the global Inbox's own props.
function EmailMessageCard({ msg, influencerEmail, defaultExpanded }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const fromName = msg.isFromUs ? msg.sentByName || msg.sentByEmail || "Us" : influencerEmail;
  const fromEmail = msg.isFromUs ? msg.sentByEmail : influencerEmail;
  const color = getNumberColor(fromEmail || fromName);

  return (
    <Box sx={{ border: "1px solid rgba(0,0,0,0.09)", borderRadius: "10px", mb: 1.5, overflow: "hidden", bgcolor: "#fff" }}>
      <Box
        onClick={() => setExpanded((e) => !e)}
        sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.4, cursor: "pointer", "&:hover": { bgcolor: "#F8F9FA" } }}
      >
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: "50%",
            bgcolor: color,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "Quicksand",
            fontWeight: 700,
            fontSize: 12.5,
            flexShrink: 0,
          }}
        >
          {initialOf(fromName)}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography noWrap sx={{ fontFamily: "Quicksand", fontWeight: 700, fontSize: 13, color: "#202124" }}>
            {fromName}
            {fromEmail && fromEmail !== fromName && (
              <Box component="span" sx={{ fontWeight: 400, color: "#5F6368" }}> &lt;{fromEmail}&gt;</Box>
            )}
          </Typography>
          {!expanded && (
            <Typography noWrap sx={{ fontFamily: "Quicksand", fontSize: 11.5, color: "#5F6368" }}>
              {cleanSnippet(msg.bodyText)}
            </Typography>
          )}
        </Box>
        {msg.attachments?.length > 0 && <AttachFileRoundedIcon sx={{ fontSize: 16, color: "#80868B", flexShrink: 0 }} />}
        <Typography sx={{ fontFamily: "Quicksand", fontSize: 11, color: "#80868B", flexShrink: 0, textAlign: "right" }}>
          {formatDateLabel(msg.sentAt)}, {formatTime(msg.sentAt)}
        </Typography>
        <IconButton size="small" sx={{ p: 0.3 }}>
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>
      {expanded && (
        <Box sx={{ px: 2, pb: 2, pt: 0.5, borderTop: "1px solid rgba(0,0,0,0.05)" }}>
          <Typography sx={{ fontFamily: "Quicksand", fontSize: 13.5, color: "#3C4043", whiteSpace: "pre-wrap", lineHeight: 1.65, mt: 1.2 }}>
            {msg.bodyText}
          </Typography>
          {msg.attachments?.length > 0 && (
            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1.5 }}>
              {msg.attachments.map((att, i) => (
                <AttachmentChip key={att.url || i} attachment={att} />
              ))}
            </Stack>
          )}
        </Box>
      )}
    </Box>
  );
}

// Collapsed by default (a "Reply" pill), same affordance EmailInboxPage.jsx uses — only
// shown at all when the logged-in viewer is the mailbox this specific thread belongs to.
function ReplyBox({ onSend, sending }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");

  const handleSend = () => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    setText("");
    setOpen(false);
    onSend(trimmed);
  };

  if (!open) {
    return (
      <Box
        onClick={() => setOpen(true)}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 1,
          border: "1px solid rgba(0,0,0,0.12)",
          borderRadius: "20px",
          px: 2,
          py: 0.9,
          cursor: "pointer",
          color: "#3C4043",
          "&:hover": { bgcolor: "#F1F3F4" },
        }}
      >
        <SendRoundedIcon fontSize="small" />
        <Typography sx={{ fontFamily: "Quicksand", fontWeight: 600, fontSize: 13 }}>Reply</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ border: "1px solid rgba(0,0,0,0.12)", borderRadius: "10px", bgcolor: "#fff" }}>
      <TextField
        fullWidth
        multiline
        minRows={3}
        maxRows={8}
        autoFocus
        placeholder="Type your reply…"
        value={text}
        onChange={(e) => setText(e.target.value)}
        sx={{ "& .MuiOutlinedInput-root": { fontFamily: "Quicksand", fontSize: 13.5, "& fieldset": { border: "none" } } }}
      />
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1.5, py: 1, borderTop: "1px solid rgba(0,0,0,0.06)" }}>
        <Typography
          onClick={() => { setOpen(false); setText(""); }}
          sx={{ fontFamily: "Quicksand", fontSize: 12.5, color: "#5F6368", cursor: "pointer", "&:hover": { color: "#202124" } }}
        >
          Discard
        </Typography>
        <IconButton
          onClick={handleSend}
          disabled={!text.trim() || sending}
          sx={{
            bgcolor: text.trim() ? EMAIL_BLUE : "rgba(0,0,0,0.06)",
            color: text.trim() ? "#fff" : "#9CA3AF",
            "&:hover": { bgcolor: text.trim() ? "#1A66D0" : "rgba(0,0,0,0.06)" },
            width: 38,
            height: 38,
          }}
        >
          {sending ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : <SendRoundedIcon sx={{ fontSize: 18 }} />}
        </IconButton>
      </Stack>
    </Box>
  );
}

function ThreadPanel({ thread, isLoading, creatorId }) {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const currentUserId = useCurrentUserId();
  const replyMutation = useSendEmailAdminReply();
  const [sending, setSending] = useState(false);
  const messages = thread?.messages || [];

  // Whichever staff member's outbound message anchors this thread, read off the message
  // itself (sentBy/sentByName/sentByEmail — see models/emailConversation.js) rather than
  // any page-level selection, since this view has no per-user switcher at all (it's every
  // user's mail for this ONE creator, not one user's mail for every creator).
  const owner = messages.find((m) => m.isFromUs && m.sentBy);
  const isOwnThread = Boolean(owner) && String(owner.sentBy) === String(currentUserId);

  const handleSend = async (message) => {
    setSending(true);
    try {
      const res = await replyMutation.mutateAsync({ conversationId: thread.conversationId, message });
      if (!res?.success) throw new Error(res?.message || "Failed to send reply");
      queryClient.invalidateQueries({ queryKey: ["creatoremail", creatorId] });
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message || error.message || "Failed to send reply", { variant: "error" });
    } finally {
      setSending(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1.5, p: 2.5 }}>
        {[...Array(4)].map((_, i) => (
          <Skeleton key={i} variant="rounded" height={i === 3 ? 120 : 56} sx={{ borderRadius: "10px" }} />
        ))}
      </Box>
    );
  }

  if (!thread) return null;

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <Box
        sx={{
          px: { xs: 1.5, sm: 2.5 },
          py: 1.5,
          borderBottom: "1px solid rgba(0,0,0,0.06)",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          background: "rgba(255,255,255,0.8)",
          backdropFilter: "blur(8px)",
          flexShrink: 0,
        }}
      >
        <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: EMAIL_BLUE, boxShadow: `0 0 0 3px ${EMAIL_BLUE}30`, flexShrink: 0 }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography noWrap sx={{ fontFamily: "Quicksand", fontWeight: 800, fontSize: 14, color: "#1C1C2E" }}>
            {thread.subject || "(no subject)"}
          </Typography>
          <Typography noWrap sx={{ fontFamily: "Quicksand", fontSize: 11, color: "#9CA3AF" }}>
            {thread.influencerEmail}{thread.planName ? ` · ${thread.planName}` : ""}
          </Typography>
        </Box>
        <Typography sx={{ fontFamily: "Quicksand", fontSize: 12, color: "#9CA3AF", ml: "auto", flexShrink: 0 }}>
          {messages.length} {messages.length === 1 ? "message" : "messages"}
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          px: { xs: 1.5, sm: 2.5 },
          py: 2,
          background: "linear-gradient(145deg, rgba(237,241,255,0.4) 0%, rgba(242,238,255,0.3) 50%, rgba(255,240,237,0.3) 100%)",
          "&::-webkit-scrollbar": { width: 4 },
          "&::-webkit-scrollbar-thumb": { bgcolor: "rgba(0,0,0,0.12)", borderRadius: 2 },
        }}
      >
        {messages.map((msg, i) => (
          <EmailMessageCard
            key={msg.messageId || `msg-${i}`}
            msg={msg}
            influencerEmail={thread.influencerEmail}
            defaultExpanded={i === messages.length - 1}
          />
        ))}
        <Box sx={{ mt: 1 }}>
          {isOwnThread ? (
            <ReplyBox onSend={handleSend} sending={sending} />
          ) : owner ? (
            <Typography sx={{ fontFamily: "Quicksand", fontSize: 12.5, color: "#9CA3AF", fontStyle: "italic" }}>
              Sent from {owner.sentByName || owner.sentByEmail}'s mailbox — replying is only available from their own inbox.
            </Typography>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}

function ThreadSidebar({ threads, selectedId, onSelect }) {
  return (
    <Box
      sx={{
        width: { xs: "100%", sm: 220, md: 260 },
        flexShrink: 0,
        borderRight: "1px solid rgba(0,0,0,0.06)",
        overflowY: "auto",
        background: "rgba(255,255,255,0.6)",
        "&::-webkit-scrollbar": { width: 3 },
      }}
    >
      <Typography
        sx={{ px: 2, pt: 2, pb: 1, fontFamily: "Quicksand", fontSize: 10, fontWeight: 800, color: "#9CA3AF", letterSpacing: 1, textTransform: "uppercase" }}
      >
        Threads
      </Typography>
      {threads.map((thread, idx) => {
        const isSelected = thread.conversationId === selectedId;
        const lastMsg = thread.messages?.[thread.messages.length - 1];
        return (
          <Box
            key={thread.conversationId || idx}
            onClick={() => onSelect(thread.conversationId)}
            sx={{
              px: 2,
              py: 1.5,
              cursor: "pointer",
              borderLeft: isSelected ? `3px solid ${EMAIL_BLUE}` : "3px solid transparent",
              bgcolor: isSelected ? `${EMAIL_BLUE}0D` : "transparent",
              transition: "all 0.15s ease",
              "&:hover": { bgcolor: `${EMAIL_BLUE}0D` },
            }}
          >
            <Typography
              noWrap
              sx={{
                fontFamily: "Quicksand",
                fontWeight: isSelected ? 800 : 700,
                fontSize: 12.5,
                color: isSelected ? EMAIL_BLUE : "#374151",
              }}
            >
              {thread.subject || "(no subject)"}
            </Typography>
            <Typography noWrap sx={{ fontFamily: "Quicksand", fontSize: 11, color: "#6B7280", mt: 0.15 }}>
              {lastMsg ? `${lastMsg.isFromUs ? `${lastMsg.sentByName || "You"}: ` : ""}${cleanSnippet(lastMsg.bodyText) || "(no content)"}` : ""}
            </Typography>
            <Typography sx={{ fontFamily: "Quicksand", fontSize: 10, color: "#9CA3AF", mt: 0.25 }}>
              {formatRelativeTime(thread.lastMessageAt)}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

function EmptyState() {
  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 1.5,
        py: 8,
        ...FADE_SLIDE,
        animation: "fadeSlideIn 0.5s ease both",
      }}
    >
      <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: "50%",
          background: `linear-gradient(135deg, ${EMAIL_BLUE}14 0%, ${EMAIL_BLUE}0A 100%)`,
          border: `2px dashed ${EMAIL_BLUE}38`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 0.5,
        }}
      >
        <MailOutlineIcon sx={{ fontSize: 28, color: EMAIL_BLUE, opacity: 0.7 }} />
      </Box>
      <Typography sx={{ fontFamily: "Quicksand", fontWeight: 800, color: "#1C1C2E", fontSize: 15 }}>
        No emails yet
      </Typography>
      <Typography sx={{ fontFamily: "Quicksand", color: "#6B7280", fontSize: 13, maxWidth: 300, textAlign: "center" }}>
        Once anyone on the team emails this creator, every thread will show up here.
      </Typography>
    </Box>
  );
}

function CreatorEmailTab({ conversations, isLoading, creatorId }) {
  // Tracked by conversationId, not list position — the list is re-sorted by lastMessageAt
  // after a reply, which would otherwise swap the view onto a different thread. Falls back
  // to the first (most recent) thread when nothing's picked yet or the pick is gone.
  const [selectedId, setSelectedId] = useState(null);
  const hasMultiple = conversations && conversations.length > 1;
  const selected = conversations?.find((c) => c.conversationId === selectedId) || conversations?.[0] || null;

  if (!isLoading && (!conversations || conversations.length === 0)) {
    return <EmptyState />;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        border: "1px solid rgba(0,0,0,0.07)",
        borderRadius: 3,
        overflow: "hidden",
        height: { xs: "auto", md: 520 },
        minHeight: { xs: 500, md: 520 },
        boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
        ...FADE_SLIDE,
        animation: "fadeSlideIn 0.35s ease both",
      }}
    >
      {hasMultiple && (
        <ThreadSidebar threads={conversations} selectedId={selected?.conversationId} onSelect={setSelectedId} />
      )}
      <ThreadPanel thread={selected} isLoading={isLoading} creatorId={creatorId} />
    </Box>
  );
}

export default CreatorEmailTab;
